(() => {
  if (customElements.get('gn-card-personalizer')) return;

  const WIDTH = 1020;
  const HEIGHT = 630;
  const designs = {
    oath: { sku: 'ORD-CARD-01', font: 'Gnome Oath', name: { x: 292, y: 501, width: 380, size: 44 }, date: { x: 650, y: 501, width: 257, size: 27 }, color: '#422b1d' },
    rites: { sku: 'ORD-CARD-02', font: 'Gnome Rites', name: { x: 326, y: 542, width: 326, size: 42 }, date: { x: 650, y: 542, width: 238, size: 26 }, color: '#294438' },
    threshold: { sku: 'ORD-CARD-03', font: 'Gnome Threshold', name: { x: 360, y: 548, width: 530, size: 36 }, date: { x: 803, y: 548, width: 245, size: 26 }, color: '#f6e8d1' },
    proverb: { sku: 'ORD-CARD-04', font: 'Gnome Proverb', name: { x: 293, y: 565, width: 440, size: 40 }, date: { x: 679, y: 565, width: 238, size: 26 }, color: '#f6e8d1' },
  };

  const loadImage = (url) => new Promise((resolve, reject) => {
    if (!url) return reject(new Error('Card artwork is missing. Please try again later.'));
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Card artwork could not be loaded. Please try again.'));
    image.src = url;
  });

  const loadFont = async (family, url) => {
    if (!url) throw new Error('The card font is missing. Please try again later.');
    const face = new FontFace(family, `url("${url}") format("woff")`);
    try {
      await face.load();
      document.fonts.add(face);
    } catch {
      throw new Error('The card font could not be loaded. Please try again.');
    }
  };

  const formatDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '2024-01-15') return '';
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
  };

  const fitSize = (ctx, value, width, maximum, minimum, font) => {
    for (let size = maximum; size >= minimum; size--) {
      ctx.font = `${size}px "${font}", Georgia, serif`;
      if (ctx.measureText(value).width <= width) return size;
    }
    return null;
  };

  const drawValue = (ctx, value, placement, color, font) => {
    if (!value) return;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    if (!fitSize(ctx, value, placement.width, placement.size, 17, font)) throw new Error('The date is too wide for this card.');
    ctx.fillText(value, placement.x, placement.y);
  };

  const drawName = (ctx, name, placement, color, font) => {
    if (!name) return;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    if (!fitSize(ctx, name, placement.width, placement.size, 16, font)) {
      ctx.font = `16px "${font}", Georgia, serif`;
    }
    ctx.fillText(name, placement.x, placement.y, placement.width);
  };

  const drawBack = (canvas, image, design, name, date) => {
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Your browser could not prepare the card preview.');
    ctx.drawImage(image, 0, 0, WIDTH, HEIGHT);
    drawName(ctx, name, design.name, design.color, design.font);
    drawValue(ctx, date, design.date, design.color, design.font);
  };

  const drawPreviewWatermark = (ctx, width, height) => {
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 6);
    ctx.globalCompositeOperation = 'difference';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.font = `700 ${Math.round(Math.min(width * 0.12, height * 0.16))}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const offset of [-height * 0.28, 0, height * 0.28]) {
      ctx.fillText('PREVIEW', 0, offset, width * 0.7);
    }
    ctx.restore();
  };

  class CardPersonalizer extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.nameInput = this.form?.querySelector('input[name="properties[Name to print]"]');
      this.dateInput = this.form?.querySelector('input[name="properties[Date of ordainment]"]');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.frontPreview = this.querySelector('[data-card-preview-front]');
      this.backPreview = this.querySelector('[data-card-preview-back]');
      this.error = this.querySelector('[role="alert"]');
      if (!this.form || !this.nameInput || !this.dateInput || !this.variantInput || !this.frontPreview || !this.backPreview || !this.error) return;
      this.images = {};
      for (const key of Object.keys(designs)) {
        const label = key[0].toUpperCase() + key.slice(1);
        this.images[key] = {
          front: loadImage(this.dataset[`front${label}`]),
          back: loadImage(this.dataset[`back${label}`]),
          font: loadFont(designs[key].font, this.dataset[`font${label}`]),
        };
        this.images[key].front.catch(() => {});
        this.images[key].back.catch(() => {});
        this.images[key].font.catch(() => {});
      }
      this.lastVariant = this.variantInput.value;
      this.nameInput.addEventListener('input', () => this.drawPreview());
      this.dateInput.addEventListener('input', () => this.drawPreview());
      this.variantPoll = setInterval(() => {
        if (this.variantInput.value === this.lastVariant) return;
        this.lastVariant = this.variantInput.value;
        this.drawPreview();
      }, 200);
      this.drawPreview();
    }

    disconnectedCallback() {
      clearInterval(this.variantPoll);
    }

    selectedKey() {
      return Object.keys(designs).find((key) => this.dataset[`variant${key[0].toUpperCase() + key.slice(1)}`] === this.variantInput.value);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async drawPreview() {
      const key = this.selectedKey();
      if (!key) return this.showError('Please select a card design.');
      const sequence = this.previewSequence = (this.previewSequence || 0) + 1;
      try {
        const [front, back] = await Promise.all([this.images[key].front, this.images[key].back, this.images[key].font]);
        if (sequence !== this.previewSequence || key !== this.selectedKey()) return;
        const frontCanvas = this.frontPreview;
        frontCanvas.width = WIDTH;
        frontCanvas.height = HEIGHT;
        const frontCtx = frontCanvas.getContext('2d');
        frontCtx.drawImage(front, 0, 0, WIDTH, HEIGHT);
        drawPreviewWatermark(frontCtx, WIDTH, HEIGHT);
        drawBack(this.backPreview, back, designs[key], this.nameInput.value.trim().replace(/\s+/g, ' '), formatDate(this.dateInput.value));
        drawPreviewWatermark(this.backPreview.getContext('2d'), WIDTH, HEIGHT);
        this.showError('');
      } catch (error) {
        this.showError(error.message);
      }
    }

  }

  customElements.define('gn-card-personalizer', CardPersonalizer);
})();
